import { useEffect, useRef, type ReactNode } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "./modal.tsx";
import styles from "./alertDialog.module.scss";

export type ChoiceVariant = "primary" | "danger" | "secondary";

export interface ChoiceOption<T> {
  label: string;
  value: T;
  variant?: ChoiceVariant;
  /** When true, this button is auto-focused on open — Enter activates it. */
  default?: boolean;
}

export interface ChoiceDialogProps<T> {
  open: boolean;
  title: string;
  body?: ReactNode;
  options: ChoiceOption<T>[];
  cancelLabel?: string;
  onCancel: () => void;
  onChoose: (value: T) => void;
}

/**
 * 3+ button decision dialog. Reuses AlertDialog chrome via the same Modal
 * primitives, so styling stays consistent across confirm and choice flows.
 * Default-focused button takes Enter; ESC / Cancel close with no choice.
 */
export function ChoiceDialog<T>({
  open,
  title,
  body,
  options,
  cancelLabel = "Cancel",
  onCancel,
  onChoose,
}: ChoiceDialogProps<T>) {
  const defaultRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the default option after Modal.showModal() runs its initial-focus
  // pass — without rAF the dialog's own focus algorithm beats us to it.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => defaultRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const variantClass = (v?: ChoiceVariant) => {
    switch (v) {
      case "danger": return styles.confirmBtnDestructive;
      case "secondary": return styles.cancelBtn;
      case "primary":
      default: return styles.confirmBtn;
    }
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <ModalHeader title={title} />
      <ModalBody>
        <div className={styles.body}>{body}</div>
      </ModalBody>
      <ModalFooter>
        <button type="button" className={styles.cancelBtn} onClick={onCancel}>
          {cancelLabel}
        </button>
        {options.map((opt, i) => (
          <button
            key={i}
            ref={opt.default ? defaultRef : undefined}
            type="button"
            className={variantClass(opt.variant)}
            onClick={() => onChoose(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </ModalFooter>
    </Modal>
  );
}

export interface AlertDialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive = red Confirm button. Default false. */
  destructive?: boolean;
  /** Disable Confirm + show busy label while awaiting onConfirm. */
  loading?: boolean;
  /** One button, focused: a warning the user must acknowledge, not a decision. No Cancel. */
  acknowledgeOnly?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation dialog for destructive or decision actions. Replaces
 * `window.confirm()` and ad-hoc Modal-based confirmers.
 *
 * Focus lands on Cancel by default so Enter doesn't accidentally confirm.
 * Prefer the `useAlertDialog()` imperative hook (`confirm({ ... })`) over
 * rendering this directly unless you already have open-state machinery.
 */
export function AlertDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  acknowledgeOnly = false,
  onCancel,
  onConfirm,
}: AlertDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus Cancel when the dialog opens — Enter on the current <dialog> then
  // triggers Cancel, not Confirm. With no Cancel, the one button.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => (acknowledgeOnly ? confirmRef : cancelRef).current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, acknowledgeOnly]);

  const resolvedConfirmLabel = confirmLabel ?? (destructive ? "Delete" : "Confirm");

  return (
    <Modal open={open} onClose={loading ? () => {} : onCancel}>
      <ModalHeader title={title} />
      <ModalBody>
        <div className={styles.body}>{body}</div>
      </ModalBody>
      <ModalFooter>
        {!acknowledgeOnly && (
          <button
            ref={cancelRef}
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
        )}
        <button
          ref={confirmRef}
          type="button"
          className={destructive ? styles.confirmBtnDestructive : styles.confirmBtn}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? "Working…" : resolvedConfirmLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}
